import { Autocomplete, TextField } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { hwApi, taskApi } from 'api';
import { subjectsApi } from 'api/subjects-api';
import { hwCreateSchema } from 'shared/helpers';
import { useModal } from 'shared/hooks';
import { HW, Option, WorkType } from 'shared/types';
import { SendToStudentsModal } from 'shared/ui';

import { classArray } from '../../constants/classArray';
import { getUser, getUserType } from '../../redux/slices/userSlice';
import { SummarizeTask, Task } from '../../shared/types/task';
import { getTeachersSubjects } from '../../utils/getTeachersSubjects';
import CheckListTasksForm from '../CheckListTasksForm';
import { LoadingError } from '../LoadingError';

import './style.scss';

interface Props {
  updateId?: string;
}

export default function HomeworkForm({ updateId }: Props) {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();
  const userTypeSelector = useSelector(getUserType);
  const user = useSelector(getUser);
  const { isOpen, close, open } = useModal();

  const [subjects, setSubjects] = useState<Option[] | null>(null);
  const [teachersSubjects, setTeachersSubjects] = useState<string[] | null>(null);
  const [tasks, setTasks] = useState<SummarizeTask[]>([]);
  const [currentHW, setCurrentHW] = useState<HW | null>(null);
  const [updateHWInitValue, setUpdateHWInitValue] = useState<HW | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);

  const formik = useFormik({
    initialValues: {
      title: updateHWInitValue?.title ?? '',
      subject: updateHWInitValue?.subject ?? null,
      studentClass: updateHWInitValue?.class ?? '',
    },
    enableReinitialize: !!updateId,
    validationSchema: hwCreateSchema,
    onSubmit: async values => {
      if (!values.subject) {
        return;
      }

      const newHW: Omit<HW, 'id'> = {
        title: values.title,
        class: values.studentClass,
        createdAt: updateHWInitValue?.createdAt ?? new Date().getTime(),
        isActive: true,
        isTemplate: setIsTemplate(),
        teacherEmail: setTeachersEmailToIcl(),
        subject: values.subject,
        type: WorkType.hw,
        isConsideration: updateHWInitValue?.isConsideration ?? false,
      };

      try {
        if (updateId || currentHW) {
          await hwApi.updateHW({ ...newHW, id: updateId ?? currentHW?.id });
        } else {
          const hw = await hwApi.addHW(newHW);
          setCurrentHW(hw);

          toast.success(t('Successfully added!'));
        }
      } catch (e) {
        console.log(e);
        toast.error(t('Something went wrong. Try again!'));
      }
    },
  });

  const setIsTemplate = () => {
    if (userTypeSelector === 'Teacher') {
      return false;
    } else if (userTypeSelector === 'NewAdmin' || userTypeSelector === 'SuperAdmin') {
      return updateHWInitValue?.isTemplate ?? false;
    } else {
      return true;
    }
  };

  const setTeachersEmailToIcl = () => {
    if (userTypeSelector === 'Teacher') {
      return user.mail;
    }
    if (userTypeSelector === 'NewAdmin' || userTypeSelector === 'SuperAdmin') {
      return updateHWInitValue ? updateHWInitValue?.teacherEmail : '';
    } else {
      return '';
    }
  };

  useEffect(() => {
    if (user) {
      getTeachersSubjects(user.mail).then(subjects => {
        setTeachersSubjects(subjects);
      });
    }
  }, [user]);

  useEffect(() => {
    if (teachersSubjects) {
      setLoading(true);

      const updateSubjects = (subjects: Option[]) => {
        const filteredSubjects = subjects.filter(subject => teachersSubjects.includes(subject.name || subject.ruName));

        if (userTypeSelector === 'Teacher') {
          setSubjects(filteredSubjects);
        } else {
          setSubjects(subjects);
        }
      };
      const unsubscribe = subjectsApi.subscribeToChanges(updateSubjects);

      if (updateId) {
        hwApi.getHW(updateId).then(data => setUpdateHWInitValue(data));
      }

      setLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [teachersSubjects]);

  useEffect(() => {
    if (updateHWInitValue && updateId) {
      setCurrentHW(updateHWInitValue);

      const setData = (data: Task[]) => setTasks(data);
      const unsubscribe = taskApi.subscribeToICLTasks({ ICLId: updateId, setNewTasks: setData });

      return () => {
        unsubscribe();
      };
    }
  }, [updateHWInitValue]);

  const openSendToStudentsModal = () => open();
  const isSendToStudentsButtonDisabled = !subjects || !tasks.length;

  return (
    <LoadingError isLoading={isLoading}>
      <FormikProvider value={formik}>
        <Form>
          <div className="mx-auto rounded-[24px] border-x-[8px] border-b-[8px] border-[#9CDFFC7F] border-opacity-50 bg-white px-2 py-8 shadow-none lg:w-11/12 lg:!px-[111px] lg:!py-10">
            <div className="flex flex-col gap-6 lg:gap-8">
              <div className="relative">
                <p className="!mb-[6px] inline-block min-w-fit text-2xl font-bold lg:!mb-2 lg:text-5xl">
                  {`${t('title')} ${t('hw')}:`}
                </p>

                <textarea
                  value={formik.values.title}
                  className={`h-[30px] w-full pt-0 text-lg lg:text-[22px] ${formik.errors.title ? 'border-red-600' : 'border-black'} border-x-0 border-b-2 border-t-0`}
                  onChange={event => formik.setFieldValue('title', event.target.value)}
                  placeholder={t('Enter the name')}
                />

                {!!formik.errors.title && (
                  <p className="absolute -bottom-[18px] left-0 !mb-0 inline-block h-fit text-sm font-medium text-red-600">
                    {t(formik.errors.title)}
                  </p>
                )}
              </div>

              <div className="purpose-wrap">
                <div className="flex flex-col !gap-4 lg:flex-row lg:gap-6">
                  <div
                    className={`relative w-full border-b ${formik.errors.studentClass ? 'border-red-600' : 'border-black'} lg:w-2/4`}
                  >
                    <p className="mb-[6px] text-sm font-bold uppercase lg:mb-2 lg:text-lg">{t('class')}:</p>

                    <Autocomplete
                      id="studentClass"
                      autoComplete={false}
                      options={classArray}
                      getOptionLabel={option => t(option)}
                      value={formik.values.studentClass}
                      onChange={(e, v) => formik.setFieldValue('studentClass', v)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          padding: 0,
                        },
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          className="border-none focus:outline-none"
                          value={t(formik.values.studentClass)}
                          error={!!formik.errors.studentClass}
                          onChange={e => formik.setFieldValue('studentClass', e.target.value)}
                          placeholder={t('Choose a class')}
                          variant="standard"
                          sx={{
                            '& .MuiInputBase-input': {
                              marginLeft: 0,
                            },
                          }}
                        />
                      )}
                    />

                    {!!formik.errors.studentClass && (
                      <p className="absolute -bottom-5 !mb-0 inline-block h-fit text-sm font-medium text-red-600">
                        {t(formik.errors.studentClass)}
                      </p>
                    )}
                  </div>

                  {subjects && (
                    <div
                      className={`relative w-full border-b ${formik.errors.subject ? 'border-red-600' : 'border-black'} lg:w-2/4`}
                    >
                      <p className="mb-[6px] text-sm font-bold uppercase lg:mb-2 lg:text-lg">{t('subject')}:</p>

                      <Autocomplete
                        id="subject"
                        options={subjects.sort((a, b) => a.name.localeCompare(b.name)) as Option[]}
                        value={formik.values.subject}
                        onChange={(event, value) => formik.setFieldValue('subject', value)}
                        getOptionLabel={option => (platformLang === 'ua' ? option.name : option.ruName)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            padding: 0,
                          },
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            className="border-0 focus:outline-none"
                            placeholder={t('Choose a subject')}
                            error={!!formik.errors.subject}
                            value={formik.values.subject?.[platformLang === 'ua' ? 'name' : 'ruName']}
                            variant="standard"
                            sx={{
                              '& .MuiInputBase-input': {
                                marginLeft: 0,
                              },
                            }}
                          />
                        )}
                      />

                      {!!formik.errors.subject && (
                        <p className="absolute -bottom-5 !mb-0 inline-block h-fit text-sm font-medium text-red-600">
                          {t(formik.errors.subject)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3 lg:flex-row lg:gap-6">
                <button
                  className="btn btn-primary more lg:w-2/6"
                  type="submit"
                  onClick={() => {
                    if (updateId || currentHW) {
                      toast.success(t('Successfully edited!'));
                    }
                  }}
                  disabled={!subjects}
                >
                  <p className="!mb-0 text-lg font-bold">{t('save')}</p>
                </button>

                <button
                  className="btn btn-primary more lg:w-2/6"
                  type="button"
                  onClick={openSendToStudentsModal}
                  disabled={isSendToStudentsButtonDisabled}
                >
                  <p className="!mb-0 text-lg font-bold">{t('send student')}</p>
                </button>
              </div>
            </div>

            {(currentHW || updateId) && (
              <CheckListTasksForm
                updateICL={!!updateId}
                currentICLId={currentHW?.id}
                workType={currentHW?.type}
                tasks={tasks}
                setTasks={setTasks}
              />
            )}
          </div>
        </Form>
      </FormikProvider>
      <SendToStudentsModal currentICL={currentHW || updateHWInitValue} isOpen={isOpen} onClose={close} />
    </LoadingError>
  );
}
